import { useState, useMemo } from 'react'
import { cn } from '@/utils'
import { useDocumentPageThemes } from '@/services/queries/document_themes'
import { ThemePreview } from '@/pages/DocumentPage/components/Documents/ThemePreviews/ThemePreview'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverPortal,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

interface ThemeSelectorProps {
  selectedThemeId?: string
  onSelect: (themeId: string) => void
}

export const ThemeSelector = ({
  selectedThemeId,
  onSelect,
}: ThemeSelectorProps) => {
  const { isLoading, data: themes } = useDocumentPageThemes()
  const [open, setOpen] = useState(false)

  const sortedThemes = useMemo(() => {
    const orgThemes = (themes || []).filter(
      (t) => t.visibility_scope !== 'global',
    )
    const desiredOrder = ['Two Image', 'One image', 'Checkbox', 'Blank']
    const globalThemes = (themes || [])
      .filter((t) => t.visibility_scope === 'global')
      .sort(
        (a, b) => desiredOrder.indexOf(a.name) - desiredOrder.indexOf(b.name),
      )
    return [...orgThemes, ...globalThemes]
  }, [themes])

  const selected = sortedThemes.find((t) => t.id === selectedThemeId)

  if (isLoading) {
    return <Skeleton className="h-12 w-48 rounded-md" />
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center justify-between w-64"
          data-testid="theme-selector-dropdown"
        >
          {selected ? selected.name : 'Select global default template'}
          {open ? (
            <ChevronUpIcon className="w-4 h-4 ml-2" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 ml-2" />
          )}
        </Button>
      </PopoverTrigger>

      <PopoverPortal>
        <PopoverContent
          className="mt-2 w-[70vw] max-w-3xl max-h-[60vh] overflow-y-auto p-4 bg-white rounded-lg shadow-lg"
          sideOffset={4}
          align="start"
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {sortedThemes.map((tmpl) => {
              const isSelected = selectedThemeId === tmpl.id
              return (
                <button
                  key={tmpl.id}
                  type="button"
                  onClick={() => {
                    onSelect(tmpl.id!)
                    setOpen(false)
                  }}
                  className={cn(
                    'h-52 flex flex-col items-center bg-white rounded-lg overflow-hidden border transition-shadow transform hover:shadow-md hover:-translate-y-0.5',
                    {
                      'border-primary-50 ring-2 ring-offset-2 ring-primary-500':
                        isSelected,
                      'border-gray-200': !isSelected,
                    },
                  )}
                  data-testid={`theme-option-${tmpl.name.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="w-full bg-gray-50 text-center text-sm font-medium py-2">
                    {tmpl.name}
                  </div>
                  <div className="w-full flex-1 aspect-w-16 aspect-h-9 bg-white">
                    <ThemePreview
                      className="w-full h-full border-none pointer-events-none"
                      documentPageThemeId={tmpl.id}
                      themeTemplateAttributes={tmpl.template_attributes}
                      themeName={tmpl.name}
                      isReadOnly
                      hideControls
                    />
                  </div>
                </button>
              )
            })}
          </div>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  )
}
